// src/pages/Projects/ProjectDetail.jsx
// -----------------------------------------------------------------------------
// Detail view for one Project, with tabs for its configurable sub-resources:
//   - Phases       (Requirement #7, #8, #10)
//   - Team         (Requirement #11)
//   - Environments (Requirement #26, #27)
//   - Statuses     (Requirement #16)
//   - Severities   (Requirement #17)
// -----------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import projectService from '../../api/projectService';
import Loader from '../../components/common/Loader';
import StatusBadge from '../../components/common/StatusBadge';
import '../Tickets/tickets.css'; // reuse the .tabs / .tab-btn styles defined for the Ticket detail page

const TABS = ['Phases', 'Team', 'Environments', 'Statuses', 'Severities'];

export default function ProjectDetail() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Phases');

  const [phases, setPhases] = useState([]);
  const [teams, setTeams] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [severities, setSeverities] = useState([]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, ph, tm, env, st, sv] = await Promise.all([
        projectService.getById(projectId),
        projectService.getPhases(projectId),
        projectService.getTeams(projectId),
        projectService.getEnvironments(projectId),
        projectService.getStatuses(projectId),
        projectService.getSeverities(projectId),
      ]);
      setProject(p.data);
      setPhases(ph.data || []);
      setTeams(tm.data || []);
      setEnvironments(env.data || []);
      setStatuses(st.data || []);
      setSeverities(sv.data || []);
    } catch (err) {
      toast.error('Failed to load project.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader label="Loading project..." />;
  if (!project) return <p>Project not found.</p>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>
            {project.projectCode} — {project.projectName}
          </h1>
          <div className="subtitle">{project.description}</div>
        </div>
        <StatusBadge value={project.status} />
      </div>

      <div className="card">
        <div className="tabs">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'Phases' && (
          <SimpleList
            items={phases}
            emptyMessage="No phases configured yet."
            renderItem={(p) => (
              <>
                <strong>{p.phaseName}</strong>
                <div className="text-muted">
                  {formatDate(p.startDate)} — {formatDate(p.endDate)}
                </div>
              </>
            )}
          />
        )}

        {activeTab === 'Team' && (
          <SimpleList
            items={teams}
            emptyMessage="No team created yet."
            renderItem={(t) => <strong>{t.teamName}</strong>}
          />
        )}

        {activeTab === 'Environments' && (
          <SimpleList
            items={environments}
            emptyMessage="No environments configured yet."
            renderItem={(e) => (
              <>
                {e.environmentName} {e.isDefaultFlag && <span className="badge badge-info">Default</span>}
              </>
            )}
          />
        )}

        {activeTab === 'Statuses' && (
          <SimpleList
            items={[...statuses].sort((a, b) => a.sequenceOrder - b.sequenceOrder)}
            emptyMessage="No statuses configured yet."
            renderItem={(s) => (
              <>
                {s.sequenceOrder}. {s.statusName}{' '}
                {s.isClosedStatusFlag && <span className="badge badge-success">Closed-type</span>}
              </>
            )}
          />
        )}

        {activeTab === 'Severities' && (
          <SimpleList
            items={[...severities].sort((a, b) => a.sequenceOrder - b.sequenceOrder)}
            emptyMessage="No severities configured yet."
            renderItem={(s) => `${s.sequenceOrder}. ${s.severityName}`}
          />
        )}
      </div>
    </div>
  );
}

// Small local helper component - renders a plain list of config rows.
function SimpleList({ items, renderItem, emptyMessage }) {
  if (!items || items.length === 0) return <p className="text-muted">{emptyMessage}</p>;
  return (
    <div>
      {items.map((item, idx) => (
        <div key={item.id || idx} style={{ padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
          {renderItem(item)}
        </div>
      ))}
    </div>
  );
}

function formatDate(isoString) {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleDateString();
}